import React, { useMemo } from "react";
import axios from "axios";
import { LayoutDashboard, ChevronDown, ChevronRight, FileText, LogOut, Folder, BarChart3 } from "lucide-react";

const Sidebar = ({
    currentUser,
    activeDept,
    activeClient,
    setActiveDept,
    setActiveClient,
    openMenus,
    setOpenMenus,
    handleLogout,
    assignedClientNames = [], // Receives list of clients with active work
    clientsList = [], // NEW: Dynamic list of clients from DB
    unreadCount = 0,
    onNotificationClick
}) => {

    // Fallback list of new clients
    const fallbackClients = [
        "A1 AMC", "AAG", "AAMS", "AMCSSC", "All Pro Amc", "Appraisal Partner", "Appraisers Review",
        "Ascribe VL", "Core Valuation", "EA - Appraisals", "Eppraisal", "Exactus",
        "Financial Dimensions", "Inspectify - Hybrid", "LRES - Appraisals", "LVS",
        "NAN", "Property valuation network", "Signature", "Team Matthew Appraisals",
        "Top Notch appraisers", "Valora AMC", "Valutrust Solutions", "Zap appraisal"
    ];

    const dynamicOtherClients = useMemo(() => {
        let list = [];
        if (!clientsList || clientsList.length === 0) {
            list = ["Proval Net", ...fallbackClients];
        } else {
            list = clientsList
                .filter(c => c.name.toLowerCase() !== "mma")
                .map(c => c.name);
        }

        list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return list;
    }, [clientsList]);

    // Safe lowercase whitespace stripping text matching block helper
    const normalizeCompare = (val) => String(val || "").trim().toLowerCase().replace(/[\s\._-]/g, "");

    // Pre-calculate an optimized Set for O(1) lookups during render cycles to eliminate rendering lag
    const assignedSet = useMemo(() => {
        return new Set(assignedClientNames.map(name => normalizeCompare(name)));
    }, [assignedClientNames]);

    return (
        <aside style={{ width: "260px", backgroundColor: "#1a2233", color: "white", display: "flex", flexDirection: "column", height: "100vh" }} className="top-filter-bar-print-hide">
            <div style={{ padding: "20px", borderBottom: "1px solid #2d3748" }}>
                <div style={{ fontWeight: "bold" }}>GO-SOURCE OPERATIONS</div>
                <div style={{ fontSize: "12px", color: "#a0aec0", marginTop: "5px" }}>
                    Logged in as: {currentUser.name} ({currentUser.role})
                </div>
            </div>

            <nav style={{ padding: "10px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Dashboard Link - Fixed at Top */}
                <div
                    onClick={() => { setActiveDept("Dashboard"); setActiveClient(""); }}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px", cursor: "pointer", borderRadius: "6px", backgroundColor: activeDept === "Dashboard" ? "#3182ce" : "transparent" }}
                >
                    <LayoutDashboard size={18} /> Dashboard
                </div>

                {/* Hourly Monitor Link */}
                <div
                    onClick={() => { setActiveDept("Hourly Monitor"); setActiveClient(""); }}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "12px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        marginTop: "5px",
                        backgroundColor: activeDept === "Hourly Monitor" ? "#3182ce" : "transparent"
                    }}
                >
                    <LayoutDashboard size={18} /> Hourly Monitor
                </div>

                {/* Notifications Link */}
                <div
                    onClick={onNotificationClick}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        marginTop: "5px",
                        backgroundColor: "transparent",
                        color: "white"
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "16px" }}>🔔</span> Notifications
                    </div>
                    {unreadCount > 0 && (
                        <span style={{
                            backgroundColor: "#ef4444",
                            color: "white",
                            borderRadius: "50%",
                            padding: "2px 6px",
                            fontSize: "10px",
                            fontWeight: "bold",
                            display: "inline-block",
                            lineHeight: "1"
                        }}>
                            {unreadCount}
                        </span>
                    )}
                </div>

                {/* Desktop Notification Support Banner */}
                {typeof window !== "undefined" && "Notification" in window && Notification.permission !== "granted" && (
                    <>
                        {Notification.permission === "denied" ? (
                            <div
                                onClick={() => alert("To receive desktop alerts:\n1. Click the lock/settings icon next to the portal address in your browser URL bar.\n2. Change 'Notifications' permission to 'Allow'.")}
                                style={{
                                    padding: "8px 12px",
                                    margin: "5px 12px",
                                    backgroundColor: "#ef444415",
                                    border: "1px solid #ef4444",
                                    color: "#f87171",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    textAlign: "center"
                                }}
                                title="Click for instructions to unblock"
                            >
                                ⚠️ Desktop Alerts Blocked
                            </div>
                        ) : (
                            <div
                                onClick={() => {
                                    Notification.requestPermission().then(permission => {
                                        if (permission === "granted") window.location.reload();
                                    });
                                }}
                                style={{
                                    padding: "8px 12px",
                                    margin: "5px 12px",
                                    backgroundColor: "#00a3e015",
                                    border: "1px solid #00a3e0",
                                    color: "#00a3e0",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    textAlign: "center"
                                }}
                            >
                                🔔 Enable Desktop Alerts
                            </div>
                        )}
                    </>
                )}

                {/* ✨ ROUTING LINK: Professional Performance Analytics Report Section */}
                {(currentUser.role === "Manager" || currentUser.role === "Admin") && (
                    <div
                        onClick={() => { setActiveDept("Report"); setActiveClient(""); }}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "12px",
                            cursor: "pointer",
                            borderRadius: "6px",
                            marginTop: "5px",
                            backgroundColor: activeDept === "Report" ? "#3182ce" : "transparent"
                        }}
                    >
                        <BarChart3 size={18} /> Reports
                    </div>
                )}

                {/* User Management Link */}
                {(currentUser.role === "Manager" || currentUser.role === "Admin") && (
                    <div
                        onClick={() => { setActiveDept("User Management"); setActiveClient(""); }}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "12px",
                            cursor: "pointer",
                            borderRadius: "6px",
                            marginTop: "5px",
                            backgroundColor: activeDept === "User Management" ? "#3182ce" : "transparent"
                        }}
                    >
                        <span style={{ fontSize: "14px", fontWeight: "bold" }}>👤 User Management</span>
                    </div>
                )}

                {/* Client Management Link */}
                {(currentUser.role === "Manager" || currentUser.role === "Admin") && (
                    <div
                        onClick={() => { setActiveDept("Client Management"); setActiveClient(""); }}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "12px",
                            cursor: "pointer",
                            borderRadius: "6px",
                            marginTop: "5px",
                            backgroundColor: activeDept === "Client Management" ? "#3182ce" : "transparent"
                        }}
                    >
                        <span style={{ fontSize: "14px", fontWeight: "bold" }}>💼 Client Management</span>
                    </div>
                )}

                {/* SCROLLABLE AREA FOR CLIENTS */}
                <div style={{ flex: 1, overflowY: "auto", marginTop: "10px" }} className="sidebar-scroll">
                    {/* LEVEL 1: Main QC Folder */}
                    <div style={{ position: "relative" }}>
                        <div onClick={() => setOpenMenus(p => ({ ...p, "QC": !p["QC"] }))} style={{ display: "flex", justifyContent: "space-between", padding: "12px", cursor: "pointer" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: "bold" }}><Folder size={18} /> QC</span>
                            {openMenus["QC"] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </div>

                        {openMenus["QC"] && (
                            <div style={{ marginLeft: "15px", borderLeft: "1px solid #2d3748", paddingLeft: "5px", position: "relative" }}>
                                {dynamicOtherClients.map((client) => {
                                    // Cross-references the memoized Set array instantly
                                    const hasWork = assignedSet.has(normalizeCompare(client));

                                    return (
                                        <div key={client} style={{ position: "relative" }}>
                                            <div
                                                onClick={() => setOpenMenus((p) => ({ ...p, [client]: !p[client] }))}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    padding: "10px 12px",
                                                    cursor: "pointer",
                                                    color: hasWork ? "white" : "#e2e8f0"
                                                }}
                                            >
                                                <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
                                                    <Folder size={15} color={hasWork ? "#f6e05e" : (openMenus[client] ? "#63b3ed" : "white")} />
                                                    {client}

                                                    {/* RED NOTIFICATION DOT */}
                                                    {hasWork && (
                                                        <span style={{
                                                            width: "7px",
                                                            height: "7px",
                                                            backgroundColor: "#f56565",
                                                            borderRadius: "50%",
                                                            marginLeft: "4px",
                                                            boxShadow: "0 0 4px #f56565"
                                                        }} />
                                                    )}
                                                </span>
                                                {openMenus[client] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </div>

                                            {/* LEVEL 3: Departments (QC, Revised) - Connected with Clean Tree Guidelines */}
                                            {openMenus[client] && (
                                                <div style={{ marginLeft: "20px", borderLeft: "1px solid #4a5568", paddingLeft: "8px", marginTop: "2px", marginBottom: "6px" }}>
                                                    {["QC", "Revised"].map((dept) => {
                                                        const isActive = activeClient === client && activeDept === dept;
                                                        return (
                                                            <div
                                                                key={dept}
                                                                onClick={() => { setActiveClient(client); setActiveDept(dept); }}
                                                                style={{
                                                                    padding: "6px 12px",
                                                                    cursor: "pointer",
                                                                    color: isActive ? "#63b3ed" : "#a0aec0",
                                                                    fontSize: "13px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: "8px",
                                                                    backgroundColor: isActive ? "rgba(49, 130, 206, 0.1)" : "transparent",
                                                                    borderRadius: "4px",
                                                                    marginTop: "2px"
                                                                }}
                                                            >
                                                                <FileText size={13} /> {dept}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Profile & Password Section */}
                <div style={{ marginTop: "auto", borderTop: "1px solid #2d3748" }}>
                    <div style={{ padding: "15px 15px 5px 15px" }}>
                        <div style={{ fontSize: "13px", fontWeight: "bold", color: "#e2e8f0" }}>{currentUser ? currentUser.name : ""}</div>
                        <div style={{ fontSize: "11px", color: "#a0aec0" }}>{currentUser ? currentUser.role : ""}</div>
                    </div>
                    <div
                        onClick={() => {
                            const curPass = prompt("Enter your current password:");
                            if (!curPass) return;
                            const newPass = prompt("Enter your new password:");
                            if (!newPass) return;
                            const token = localStorage.getItem("token");
                            axios.put("/api/users/change-password", {
                                currentPassword: curPass,
                                newPassword: newPass
                            }, {
                                headers: { Authorization: `Bearer ${token}` }
                            }).then(res => {
                                if (res.data?.success) alert("Password changed successfully!");
                            }).catch(err => {
                                alert(err.response?.data?.message || "Failed to change password.");
                            });
                        }}
                        style={{ padding: "5px 15px 15px 15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#90cdf4" }}
                    >
                        🔑 Change Password
                    </div>
                </div>

                {/* Logout Button */}
                <div onClick={handleLogout} style={{ padding: "15px", borderTop: "1px solid #2d3748", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", color: "#feb2b2" }}>
                    <LogOut size={18} /> Logout
                </div>
            </nav>

            <style>
                {`
                    .sidebar-scroll::-webkit-scrollbar { width: 4px; }
                    .sidebar-scroll::-webkit-scrollbar-thumb { background: #2d3748; border-radius: 10px; }
                    .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
                `}
            </style>
        </aside>
    );
};

export default Sidebar;